import { logDebug, logError } from './logService';

// Configuração do cache
const CACHE_CONFIG = {
  habits: {
    ttl: 5 * 60 * 1000, // 5 minutos
    maxSize: 100 // máximo de 100 hábitos em cache
  },
  user: {
    ttl: 15 * 60 * 1000, // 15 minutos
    maxSize: 10 // máximo de 10 usuários em cache
  }
};

class Cache {
  constructor(config) {
    this.data = new Map();
    this.config = config;
    this.lastSync = new Map();
  }

  // Verifica se o cache está válido
  isValid(key, type) {
    const lastSync = this.lastSync.get(key);
    if (!lastSync) return false;

    const now = Date.now();
    const ttl = this.config[type].ttl;
    return (now - lastSync) < ttl;
  }

  // Obtém dados do cache
  get(key, type) {
    if (!this.isValid(key, type)) {
      return null;
    }
    return this.data.get(key);
  }

  // Armazena dados no cache
  set(key, value, type) {
    // Limpa entradas antigas se necessário
    this.cleanup(type);

    this.data.set(key, value);
    this.lastSync.set(key, Date.now());

    logDebug('Cache atualizado', { type, key });
  }

  // Limpa entradas antigas
  cleanup(type) {
    const maxSize = this.config[type].maxSize;
    const entries = Array.from(this.data.entries())
      .filter(([key]) => key.startsWith(`${type}:`));

    if (entries.length >= maxSize) {
      // Remove as entradas mais antigas
      entries
        .sort(([, a], [, b]) => this.lastSync.get(a) - this.lastSync.get(b))
        .slice(0, entries.length - maxSize + 1)
        .forEach(([key]) => {
          this.data.delete(key);
          this.lastSync.delete(key);
        });
    }
  }

  // Invalida o cache para uma chave específica
  invalidate(key) {
    this.data.delete(key);
    this.lastSync.delete(key);
    logDebug('Cache invalidado', { key });
  }

  // Invalida todo o cache
  clear() {
    this.data.clear();
    this.lastSync.clear();
    logDebug('Cache limpo completamente');
  }
}

// Instância única do cache
const cache = new Cache(CACHE_CONFIG);

// Funções de conveniência para hábitos
export const getHabitsFromCache = (userId) => {
  const key = `habits:${userId}`;
  return cache.get(key, 'habits');
};

export const setHabitsInCache = (userId, habits) => {
  const key = `habits:${userId}`;
  cache.set(key, habits, 'habits');
};

export const invalidateHabitsCache = (userId) => {
  const key = `habits:${userId}`;
  cache.invalidate(key);
};

// Funções de conveniência para usuários
export const getUserFromCache = (userId) => {
  const key = `user:${userId}`;
  return cache.get(key, 'user');
};

export const setUserInCache = (userId, userData) => {
  const key = `user:${userId}`;
  cache.set(key, userData, 'user');
};

export const invalidateUserCache = (userId) => {
  const key = `user:${userId}`;
  cache.invalidate(key);
};

// Função para limpar todo o cache
export const clearCache = () => cache.clear();

// Função para obter estatísticas do cache
export const getCacheStats = () => {
  return {
    totalEntries: cache.data.size,
    habitsEntries: Array.from(cache.data.keys()).filter(k => k.startsWith('habits:')).length,
    userEntries: Array.from(cache.data.keys()).filter(k => k.startsWith('user:')).length,
    lastSyncs: Object.fromEntries(cache.lastSync)
  };
};